import React, { Component } from 'react'
import { i18nTranslate } from 'src/utils'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import { Helmet } from 'react-helmet'
import {
  enableStorePickup,
  enableOutfits,
  isMerchandising,
  APPConfig,
} from 'config/appConfig'
import {
  ApiContent,
  tryJSONParse,
  checkIsSaleStarted,
  trackEnhancedProductDetailImpression,
} from 'src/utils'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'
import { productSchema, nlxxConfigContainer } from 'src/models'
// Direct component imports (corrected relative paths)
import { EmptyProduct } from '../EmptyProduct'
import { BreadCrumb } from '../BreadCrumb'
import { BundlePDP, BundleFloatingBar, V2BundleFloatingBar } from '../BundlePDP'
import { CollectionProduct } from '../CollectionProduct'
import { StorePickup, storePickupState } from '../StorePickup'
import { toastState } from '../Toast'
import { OutFitsModel } from 'src/views/components/OutfitComponents/OutFitsModel'
import { RemoveModal } from 'src/views/components/OutfitComponents/RemoveModal'
import { catalogDeps, productDeps } from 'src/deps'
import { productState } from './ProductState'
import { enablePdpRedesign } from 'src/utils/commonUtils'
import './styles.scss'
import MetaDecorator from '../MetaDecorator'

@observer
class ProductComponent extends Component {
  static contextType = ApiContent
  @observable quantity = 1
  backgroundRefreshInterval

  constructor(props, context) {
    super(props)
    if (typeof window !== 'undefined') {
      console.log('Loop [ProductComponent] CONSTRUCTOR', {
        key: props.key || '',
        path: props?.location?.pathname || '',
        search: props?.location?.search || '',
        match: props?.match || {},
        stack: new Error().stack || '',
      })
    }
    this.context = context
    this.contextName = 'productData'
    this.curLocaleFromUrl = getLocaleCodeFromUrl({
      locationProps: props,
    })
    if (typeof window !== 'undefined') {
      const url = props?.location?.pathname + props?.location?.search
      if (
        window.isCurrentConstructorUrl === undefined ||
        window.isCurrentConstructorUrl !== url
      ) {
        console.log(
          '[Debug Looping] first time for this url, calling getDataFromSource'
        )
        window.isCurrentConstructorUrl = url
        !isMerchandising && this.getDataFromSource()
      } else {
        // Same URL constructor called again — skip data fetch instead of
        // reloading the page. The previous window.location.reload() here
        // caused infinite hard-reload loops when the component was
        // unmounted/remounted without a URL change (e.g. due to parent
        // re-renders or route key instability).
        console.log('[Debug Looping] existing url, skipping duplicate fetch')
      }
    } else {
      // If not in browser environment, call getDataFromSource directly
      !isMerchandising && this.getDataFromSource()
    }
  }

  getDataFromSource = () => {
    // Load NLXX configuration before checking shipping restrictions (SSR)
    nlxxConfigContainer.loadNLXXConfig()

    const productData = catalogDeps.getProductResponseFromContext({
      context: this.context,
      contextName: this.contextName,
      props: { curLocaleFromUrl: this.curLocaleFromUrl, ...this.props },
    })

    const skuId = this.props?.location?.queryParams?.skuId || ''
    const productIdFromURL = this.props?.match?.params?.productId || '110001'
    const productIdFromResponse = productData?.product?.[0]?.identifier
    const semanticId =
      productData?.product?.[0]?.semantics?.length > 0
        ? productData?.product?.[0]?.semantics?.find(
            semanticId => productIdFromURL == semanticId
          )
        : ''
    const slug = productData?.product?.[0]?.properties?.slug
    if (
      productData &&
      (productIdFromURL === productIdFromResponse ||
        productIdFromURL === semanticId ||
        productIdFromURL === slug)
    ) {
      productState.constructDataAndSetProductResponse(
        productData,
        skuId,
        this.props
      )
      productState.checkAndNotifyShippingRestriction(productData)
      trackEnhancedProductDetailImpression(productData)
    } else {
      productState.getProduct(this.props, {
        context: this.context,
        contextName: this.contextName,
      })
    }
  }

  componentDidUpdate(prevProps) {
    if (
      JSON.stringify(prevProps?.location) !==
      JSON.stringify(this.props?.location)
    ) {
      productState.getProduct(this.props)
    }
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      console.log('Loop [ProductComponent] MOUNT', {
        key: this.props.key || '',
        path: this.props?.location?.pathname || '',
        search: this.props?.location?.search || '',
        match: this.props?.match || {},
        stack: new Error().stack || '',
      })
    }
    // Load NLXX configuration for CSR cases (when productState.getProduct is called directly)
    if (
      !nlxxConfigContainer.nlxxConfigStoreId ||
      nlxxConfigContainer.nlxxConfigStoreId !== storeContainer.activeStoreId
    ) {
      nlxxConfigContainer.loadNLXXConfig()
    }
    this.registerTimerForSale()
  }

  componentWillUnmount() {
    console.log('[Debug Looping] unmount')
    if (typeof window !== 'undefined') {
      window.isCurrentConstructorUrl = undefined
    }
    productState.resetValues()
    this.clearSaleTimer()
  }

  clearSaleTimer = () => {
    if (this.backgroundRefreshInterval) {
      clearInterval(this.backgroundRefreshInterval)
    }
  }

  registerTimerForSale = () => {
    let saleEventStartTime
    const eventProperties = checkIsSaleStarted()

    if (eventProperties?.isSaleRegistered) {
      const intervalTime = 1000
      this.backgroundRefreshInterval = setInterval(() => {
        const eventProperties = checkIsSaleStarted()
        if (eventProperties?.isSaleStarted) {
          this.backgroundRefresh()
        } else if (
          eventProperties?.isSaleInProgress ||
          eventProperties?.isSaleEnded
        ) {
          this.clearSaleTimer()
        }
      }, intervalTime)
    }
  }

  backgroundRefresh = async () => {
    this.clearSaleTimer()
    await productState.getProduct(this.props)
    toastState.setToastMessage(
      i18nTranslate('pdp.saleStarted', 'Event has started', {
        nameSpace: 'ssr-resource',
      }),
      true
    )
    productState.handleQuantityChangePromotion(
      productState.product,
      productState.productCount
    )
  }

  handleAddToBag = () => {
    if (document.body.classList.contains('no-scroll')) {
      document.body.classList.remove('no-scroll')
    }
    productState.handleAddToBag()
  }

  handleSkuSelection = product => {
    productState.updateSku(product)
    productState.handleQuantityChangePromotion(
      productState.product,
      productState.productCount
    )
  }

  selectSkuById = skuId => {
    productState.selectSkuById(skuId)
  }

  handleQuantityChange = count => {
    productState.productCount = count
    this.quantity = count
    this.props?.onSubscriptionQuantityChange &&
      this.props?.onSubscriptionQuantityChange(count)
  }

  renderCollection = () => {
    return <CollectionProduct product={productState.product?.subProducts} />
  }

  setPickUpType = type => {
    productState.setPickUpType(type)
  }
  renderBundlePriceAndButtons = (isExpressPDP = false) => {
    const product = productState.product || {}
    const inventory = productState.inventory || {}
    const inventoryStatus = productState.inventoryStatus || []
    const currency = product?.currency || ''
    const bundleType = product?.type || 'product'
    const transactionPrice = product?.transactionPrice
    const isBundleView = this.props?.isBundleView || false
    const handleBundleView = this.props?.handleBundleView || {}
    return (
      <V2BundleFloatingBar
        {...this.props}
        isExpressPDP={this.props?.isExpressPDP || isExpressPDP}
        product={product}
        handleQuantityChange={this.handleQuantityChange}
        productCount={this.quantity}
        handleAddToBag={this.handleAddToBag}
        transactionPrice={transactionPrice}
        currency={currency}
        bundleType={bundleType}
        price={product?.transactionPrice?.bundlePrice || ''}
        isBundleView={isBundleView}
        handleBundleView={handleBundleView}
        inventory={inventory}
        inventoryStatus={inventoryStatus}
      />
    )
  }

  renderBundleData = (isExpressPDP = false) => {
    const product = productState.product || {}
    const inventory = productState.inventory || {}
    const inventoryStatus = productState.inventoryStatus || []
    const skuLevelSubProductStatus = productState.skuLevelSubProductStatus || []
    const currency = product?.currency || ''
    const bundleType = product?.type || 'product'
    const transactionPrice = product?.transactionPrice
    const isBundleView = this.props?.isBundleView || false
    const handleBundleView = this.props?.handleBundleView || {}
    return (
      <>
        <BundlePDP
          {...this.props}
          product={product}
          bundleType={bundleType}
          isBundleView={isBundleView}
          inventory={inventory}
        />
        <BundleFloatingBar
          {...this.props}
          product={product}
          handleQuantityChange={this.handleQuantityChange}
          productCount={this.quantity}
          handleAddToBag={this.handleAddToBag}
          transactionPrice={transactionPrice}
          currency={currency}
          bundleType={bundleType}
          price={product?.transactionPrice?.bundlePrice || ''}
          isBundleView={isBundleView}
          handleBundleView={handleBundleView}
          inventory={inventory}
          inventoryStatus={inventoryStatus}
          skuLevelSubProductStatus={skuLevelSubProductStatus}
        />
      </>
    )
  }

  render() {
    const {
      product = {},
      productResponse = {},
      productCount = 1,
      inventory = {},
      subscribeAndSave = 0,
    } = productState
    const { status = '', category = {} } = productResponse
    const { name: productName, description: metaDescription } = product
    const breadCrumbList = productDeps.getBreadCrumbList(category, productName)
    let backToCategoryLabel = ''
    let backToCategoryLink = ''
    if (Array.isArray(breadCrumbList) && breadCrumbList.length >= 3) {
      const categoryCrumb = breadCrumbList[breadCrumbList.length - 2] || {}
      backToCategoryLabel = categoryCrumb.label || ''
      backToCategoryLink = categoryCrumb.value || ''
    }
    const currency = productState.product?.currency || ''
    const productSchemaData = {
      ...productState.product,
      currency,
      location: this.props?.location,
    }
    const schemaData = productSchema(productSchemaData)
    const titlePDP = productName ? productName : 'Product Not Found'
    const slug = productResponse?.product?.properties?.slug
    const getSeoInformation = attributeDesc => {
      return tryJSONParse(attributeDesc, {}) || {}
    }
    const metaAssets =
      getSeoInformation(product?.properties?.seoInformation) || {}
    const metaTitlePDPInit =
      (metaAssets?.metaTitle ? metaAssets?.metaTitle : titlePDP) || ''
    const metaTitlePDP =
      metaTitlePDPInit
        ?.replace?.(' | Nu Skin', '')
        ?.replace?.('Nu Skin | ', '') + ' | Nu Skin'
    const enableNewPDPStyle =
      APPConfig?.getAppConfig()?.enableNewPDPStyle === 'true'
    return (
      <>
        <MetaDecorator
          title={metaTitlePDP}
          imageUrl=""
          imageAlt=""
          description={metaAssets?.metaDescription || ''}
          canonicalUrl={metaAssets?.canonicalURL || ''}
          location={this.props}
        />
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
          {enableNewPDPStyle && (
            <link
              href={
                'https://fonts.googleapis.com/css2?family=Lora:wght@400;700'
              }
              rel="stylesheet"
            />
          )}
          {enableNewPDPStyle && (
            <link
              href={
                'https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800'
              }
              rel="stylesheet"
            />
          )}
        </Helmet>
        <BreadCrumb
          forceBackToCategory
          breadCrumbList={breadCrumbList}
          backToCategoryLabel={backToCategoryLabel}
          backToCategoryLink={backToCategoryLink}
        />
        <div
          id="pdp-content"
          className={`${this.template ? this.template : ''} ${
            enableNewPDPStyle ? 'v2-pdp-style' : ''
          }`}>
          {status &&
            (status === 'failure' ? (
              <EmptyProduct />
            ) : (
              <>
                {enableOutfits && (
                  <>
                    <RemoveModal />
                    <OutFitsModel />
                  </>
                )}
                {enableStorePickup && (
                  <StorePickup show={storePickupState?.showStorePickup} />
                )}
                {this.renderProductView(
                  productResponse,
                  product,
                  productCount,
                  inventory,
                  subscribeAndSave
                )}
              </>
            ))}
        </div>
      </>
    )
  }
}

export { ProductComponent }
